import { useMutation, useQuery } from "@apollo/client";
import Add from "@mui/icons-material/Add";
import { Button, Stack, Typography } from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import {
  AddEditDocumentDialog,
  AddEditDocumentDialogState,
  defaultAddEditDocumentDialogState,
} from "components/Document";
import {
  DocumentUploadSection,
  SubmissionDocument,
} from "components/Submission/DocumentUploadSection";
import {
  DocumentStatus,
  DocumentType,
  GetSubmissionDocumentsStepQuery,
  SubmissionDocumentInput,
  SubmissionDocumentType,
  SubmissionStepType,
} from "generated-graphql/graphql";
import { useCurrentUser } from "hooks/useCurrentUser";
import { useDocumentDownloadLink } from "hooks/useDocumentDownloadLink";
import { useTenant } from "hooks/useTenant";
import { useWasteStepper } from "hooks/useWasteStepper";
import { UPDATE_SUBMISSION_MUTATION } from "queries/submissions";
import { useCallback, useState } from "react";

import { GET_SUBMISSION_DOCUMENTS_STEP_QUERY } from "../queries";
import { StepHeader } from "../StepHeader";
import { StepSubHeader } from "../StepSubHeader";
import { StepContinueFooter } from "./StepContinueFooter";

type DocumentRow = NonNullable<
  NonNullable<GetSubmissionDocumentsStepQuery["submission"]>["documents"]
>[number];

function isAttachment(type: SubmissionDocumentType) {
  return type === SubmissionDocumentType.Attachment;
}

/** Map a server row to the upload table's display shape. */
function toDisplayDocument(row: DocumentRow): SubmissionDocument {
  return {
    id: row.document.id,
    submissionDocumentId: row.id,
    name: row.document.title || "Untitled",
    documentType: row.document.documentType ?? DocumentType.Other,
    submissionDocumentType: row.type,
    status: row.status ?? undefined,
    url: row.document.storageLink ?? undefined,
    fileExtension: row.document.fileExtension,
  };
}

/** Map a server row back to the replace-semantics mutation input. */
function toDocumentInput(row: DocumentRow): SubmissionDocumentInput {
  return {
    documentId: row.document.id,
    type: row.type,
    status: row.status ?? undefined,
  };
}

/**
 * Prepare Document step (document forms): staff attach the filled-out
 * submission document plus any supporting attachments. Each add/delete
 * persists immediately through `updateSubmission`'s documents-only payload
 * (matching the Tier II report Documents step); the footer's Save only
 * touches the step. The payload is replace-semantics, so every save sends
 * both full arrays — signed/rejected rows always pass through untouched and
 * are never deletable here.
 */
export function PrepareDocumentStep({
  submissionId,
}: {
  submissionId: string;
  facilityId: string;
}) {
  const alerts = useAlerts();
  const { tenantId } = useTenant();
  const { isStaff } = useCurrentUser();
  const { canEdit } = useWasteStepper();
  // Document editing is staff-only server-side (`updateSubmission` denies
  // document-form edits for non-staff); the extra isStaff keeps this gate
  // aligned with the server even if the shell's canEdit policy widens.
  const canEditDocuments = canEdit && isStaff;

  const { data } = useQuery(GET_SUBMISSION_DOCUMENTS_STEP_QUERY, {
    variables: { id: submissionId },
    fetchPolicy: "cache-and-network",
  });
  const rows = data?.submission?.documents ?? [];
  const submissionRows = rows.filter((row) => !isAttachment(row.type));
  const attachmentRows = rows.filter((row) => isAttachment(row.type));

  const [updateSubmission, { loading: saving }] = useMutation(UPDATE_SUBMISSION_MUTATION, {
    refetchQueries: [GET_SUBMISSION_DOCUMENTS_STEP_QUERY],
    awaitRefetchQueries: true,
  });

  // Replace-semantics save: always send BOTH full arrays, with the one change
  // applied — dropping a row here deletes its association server-side.
  const persistDocuments = useCallback(
    async (
      submissionDocuments: SubmissionDocumentInput[],
      attachmentDocuments: SubmissionDocumentInput[]
    ) => {
      try {
        await updateSubmission({
          variables: { input: { id: submissionId, submissionDocuments, attachmentDocuments } },
        });
      } catch (error) {
        alerts.error("Error saving documents.", error);
      }
    },
    [updateSubmission, submissionId, alerts]
  );

  const [dialogState, setDialogState] = useState<AddEditDocumentDialogState>(
    defaultAddEditDocumentDialogState
  );
  const [addingType, setAddingType] = useState<SubmissionDocumentType>(
    SubmissionDocumentType.Submission
  );

  const handleAdd = (type: SubmissionDocumentType) => {
    setAddingType(type);
    setDialogState({ ...defaultAddEditDocumentDialogState, open: true });
  };

  // The dialog uploads/creates the Document; associating it with the
  // submission is this immediate save.
  const handleDocumentSubmit = async (document: { id: string }) => {
    const added: SubmissionDocumentInput = {
      documentId: document.id,
      type: addingType,
      // Matches the legacy form: a new submission document starts as a draft;
      // attachments carry no status.
      status: isAttachment(addingType) ? undefined : DocumentStatus.Draft,
    };
    await persistDocuments(
      [...submissionRows.map(toDocumentInput), ...(isAttachment(addingType) ? [] : [added])],
      [...attachmentRows.map(toDocumentInput), ...(isAttachment(addingType) ? [added] : [])]
    );
  };

  const handleDelete = async (documentId: string) => {
    await persistDocuments(
      submissionRows.filter((row) => row.document.id !== documentId).map(toDocumentInput),
      attachmentRows.filter((row) => row.document.id !== documentId).map(toDocumentInput)
    );
  };

  // Row-name click: open the stored file in a new tab via a short-lived link.
  const [getDocumentDownloadLink] = useDocumentDownloadLink();
  const handleOpenDocument = async (doc: SubmissionDocument) => {
    try {
      const { data: linkData } = await getDocumentDownloadLink({ variables: { id: doc.id } });
      if (!linkData?.getDocumentDownloadLink) {
        throw new Error("No download link returned");
      }
      window.open(linkData.getDocumentDownloadLink, "_blank", "noopener");
    } catch (error) {
      alerts.error("Error opening document.", error);
    }
  };

  // Signed/rejected rows are preserved by every save and surface read-only;
  // deleting one would sabotage the signing trail.
  const canDeleteSubmissionRow = (doc: SubmissionDocument) =>
    canEditDocuments && doc.submissionDocumentType === SubmissionDocumentType.Submission;
  const canDeleteAttachmentRow = () => canEditDocuments;

  return (
    <Stack spacing={3}>
      <StepHeader
        title="Prepare Document"
        description="Attach the filled-out submission document and any supporting attachments."
      />
      <Stack spacing={1}>
        <StepSubHeader
          title="Submission document"
          action={
            canEditDocuments && (
              <Button
                startIcon={<Add />}
                disabled={saving}
                onClick={() => handleAdd(SubmissionDocumentType.Submission)}
              >
                Add Document
              </Button>
            )
          }
        />
        {submissionRows.length > 0 ? (
          <DocumentUploadSection
            documents={submissionRows.map(toDisplayDocument)}
            onDeleteDocument={handleDelete}
            onSelectDocument={handleOpenDocument}
            canDeleteDocument={canDeleteSubmissionRow}
            tenantId={tenantId ?? undefined}
          />
        ) : (
          <Typography variant="body2" color="text.secondary">
            No submission document has been added.
          </Typography>
        )}
      </Stack>
      <Stack spacing={1}>
        <StepSubHeader
          title="Additional attachments (optional)"
          action={
            canEditDocuments && (
              <Button
                startIcon={<Add />}
                disabled={saving}
                onClick={() => handleAdd(SubmissionDocumentType.Attachment)}
              >
                Add Attachment
              </Button>
            )
          }
        />
        {attachmentRows.length > 0 ? (
          <DocumentUploadSection
            documents={attachmentRows.map(toDisplayDocument)}
            onDeleteDocument={handleDelete}
            onSelectDocument={handleOpenDocument}
            canDeleteDocument={canDeleteAttachmentRow}
            tenantId={tenantId ?? undefined}
          />
        ) : (
          <Typography variant="body2" color="text.secondary">
            No attachments have been added.
          </Typography>
        )}
      </Stack>
      <StepContinueFooter
        submissionId={submissionId}
        step={SubmissionStepType.PrepareDocument}
        backStep={SubmissionStepType.Understand}
      />
      {dialogState.open && (
        <AddEditDocumentDialog
          dialogState={dialogState}
          setDialogState={setDialogState}
          tenantId={tenantId || ""}
          showFacilityPicker={false}
          refetchQueries={[]}
          onSubmit={handleDocumentSubmit}
        />
      )}
    </Stack>
  );
}
